import { BASE_URL } from '@service/RequestService';
import { DATE_FORMAT, DATE_WITH_TIME_FORMAT } from '@utils/constants';
import dayjs from 'dayjs';

export const dateFormatTimeLine = (
  key: string,
  data: { count: number; time: string }[] | undefined,
  date: {
    startDate: string;
    finishDate: string;
  }
) => {
  const dataConvert: { [key: string]: { count: number; time: string }[] } = {
    DAY: Array.from({
      length: dayjs(date.startDate, DATE_FORMAT).endOf('month').date()
    }).map((_, index) => ({
      time: dayjs(date.startDate, DATE_FORMAT)
        .date(index + 1)
        .format(DATE_WITH_TIME_FORMAT),
      count: 0
    })),
    MONTH: Array.from({ length: 12 }).map((_, index) => ({
      time: dayjs(date.startDate, DATE_FORMAT).date(1).month(index).format(DATE_WITH_TIME_FORMAT),
      count: 0
    })),
    YEAR: Array.from({ length: 5 }).map((_, index) => ({
      time: dayjs(date.finishDate, DATE_FORMAT)
        .date(1)
        .month(0)
        .add(index + 1 - 5, 'year')
        .format(DATE_WITH_TIME_FORMAT),
      count: 0
    }))
  };

  return dataConvert[key]?.map(item => {
    if (data) {
      const findData = data?.find(value => value?.time === item?.time)!;

      return {
        time: item?.time,
        count: findData?.count || 0
      };
    }

    return item;
  });
};

export const generateWidgetUrl = ({
  type,
  startDate,
  finishDate
}: {
  type?: string;
  startDate?: string | null;
  finishDate?: string | null;
}) => {
  if (!type) return '';

  const endWeek = dayjs().endOf('week').add(1, 'day').format(DATE_FORMAT);
  const startWeek = dayjs().startOf('week').add(1, 'day').format(DATE_FORMAT);

  startDate = startDate || startWeek;
  finishDate = finishDate || endWeek;

  const BASE_PATHS: Record<string, string> = {
    LIST: `${BASE_URL}/v1/dashboard/widget/table`,
    NUMBER: `${BASE_URL}/v1/dashboard/widget/number`,
    CHART: `${BASE_URL}/v1/dashboard/widget/chart/v2`,
    PIE_CHART: `${BASE_URL}/v1/dashboard/widget/chart/v2`,
    TIMELINE: `${BASE_URL}/v1/dashboard/widget/time-line?startDate=${startDate}&finishDate=${finishDate}`
  };

  return BASE_PATHS?.[type] || '';
};

export function parseJSON(value?: string) {
  if (value) {
    const fixedKeys = value?.replace(/([a-zA-Z0-9_]+)\s*:/g, '"$1":');

    let fixedJson = fixedKeys.replace(/:\s*'([^']*)'/g, (match, value) => {
      const sanitizedValue = value.replace(/'/g, "\\'");

      return `:"${sanitizedValue}"`;
    });

    fixedJson = fixedJson.replace(/\\'/g, "'");

    try {
      return JSON.parse(fixedJson);
    } catch (error) {
      return {};
    }
  }

  return {};
}

export const getFirstParam = <T extends object>(firstEventListParams: T) =>
  Object.keys(firstEventListParams)?.reduce<typeof firstEventListParams>(
    (p, c) => {
      if (Array.isArray(firstEventListParams[c as keyof typeof firstEventListParams])) {
        p[c as keyof typeof firstEventListParams] = (
          firstEventListParams[c as keyof typeof firstEventListParams] as string[]
        ).filter(item => item && item !== 'null' && item !== 'undefined') as any;
      } else {
        p[c as keyof typeof firstEventListParams] = firstEventListParams[c as keyof typeof firstEventListParams] as any;
      }

      return p;
    },
    {} as typeof firstEventListParams
  );

export const getSecondParam = <T extends object, U extends object>(firstEventListParams: T, secondEventListParams: U) =>
  Object.keys(firstEventListParams)?.reduce<typeof firstEventListParams>(
    (p, c) => {
      const key = `two${c.charAt(0).toUpperCase() + c.slice(1)}`;

      if (Array.isArray(secondEventListParams[key as keyof typeof secondEventListParams])) {
        p[c as keyof typeof firstEventListParams] = (
          secondEventListParams[key as keyof typeof secondEventListParams] as string[]
        ).filter(item => item && item !== 'null' && item !== 'undefined') as any;
      } else {
        p[c as keyof typeof firstEventListParams] = secondEventListParams[
          key as keyof typeof secondEventListParams
        ] as any;
      }

      return p;
    },
    {} as typeof firstEventListParams
  );

export const parserLinkFunc = (value: any): any[] =>
  Object.entries(value).map(([key, value]) => {
    if (Array.isArray(value)) {
      return value.map(item => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`).join('&');
    }

    if (key === 'partTwoFilter') {
      return parserLinkFunc(
        Object.keys(value as object)?.reduce(
          (prev, cur) => {
            if (!['page', 'size'].includes(cur)) {
              return {
                ...prev,
                [`two${cur.charAt(0).toUpperCase() + cur.slice(1)}`]: value?.[cur as keyof typeof value]
              };
            }

            return prev;
          },
          {
            isSecondStep: 1
          }
        )
      ).join('&');
    }

    return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
  });
